import { Link } from '@tanstack/react-router'
import { Button, Input, Spin, Tooltip } from 'antd'
import { handlePreferredPlanToggle } from 'features/fde'
import { Check, DollarSign, Edit3, Plus, X } from 'lucide-react'
import React, { useMemo, useState } from 'react'
import styled from 'styled-components'
import { GeckoAssetHealthAction, GeckoAssetHealthPlan } from 'types'

import { ActionsList, CreatePlanForm } from './components'
import { RiskBadge } from './components/RiskBadge'
import { useHealthPlanningWidget } from './hooks'
import { HealthPlanningWidgetProps } from './types'

const WidgetContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  background: #333333;
  border-radius: 0.5rem;
  padding: 0.75rem;
`

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 1.5rem;
`

const ErrorContainer = styled.div`
  padding: 1rem;
  background: ${({ theme }) => theme.forecast.redGlassBackground};
  border: 0.0625rem solid ${({ theme }) => theme.forecast.danger};
  border-radius: 0.25rem;
  color: ${({ theme }) => theme.forecast.danger};
  font-size: 0.875rem;
`

const PlansContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

const PlanRow = styled.div<{ $selected?: boolean }>`
  display: grid;
  grid-template-columns: 1rem 1fr 3.75rem 5rem auto;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.5rem;
  background: ${({ theme }) => theme.forecast.cardBackground};
  border-radius: 0.375rem;
  border: 0.0625rem solid
    ${({ $selected }) => ($selected ? '#ffffff' : 'transparent')};
  cursor: pointer;
  font-size: 0.8125rem;
  transition: all 0.2s ease;

  &:hover {
    background: ${({ theme }) => theme.forecast.hoverButton};
  }
`

const PreferredSelector = styled.div<{ $preferred: boolean }>`
  width: 0.75rem;
  height: 0.75rem;
  border-radius: 50%;
  border: 0.125rem solid
    ${({ $preferred }) => ($preferred ? '#22c55e' : '#6b7280')};
  background: ${({ $preferred }) => ($preferred ? '#22c55e' : 'transparent')};
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;

  &:hover {
    border-color: ${({ $preferred }) => ($preferred ? '#16a34a' : '#9ca3af')};
  }
`

const PlanNameContainer = styled.div`
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const PlanName = styled.div<{ $editing?: boolean; $isLong?: boolean }>`
  font-weight: 600;
  color: ${({ theme }) => theme.forecast.text1};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
  font-size: ${({ $isLong }) => ($isLong ? '0.6875rem' : '0.8125rem')};
  line-height: 1.2;
  ${({ $editing }) => $editing && 'display: none;'}
`

const PlanEditInput = styled(Input)`
  background-color: #363636;
  color: #ffffff;
  border: none;
  border-radius: 0.25rem;
  font-size: 0.8125rem;
  height: 1.5rem;

  &::placeholder {
    color: #898989;
  }

  &:focus {
    box-shadow: 0 0 0 0.125rem rgba(34, 197, 94, 0.2);
    border-color: #22c55e;
  }
`

const ActionCount = styled.span`
  text-align: center;
  color: ${({ theme }) => theme.forecast.secondaryText};
  font-size: 0.75rem;
`

const StyledRiskBadge = styled(RiskBadge)`
  display: inline-block;
  min-width: 4.375rem;
  padding: 0.2em 0.7em;
  border-radius: 0.375rem;
  font-weight: 700;
  font-size: 0.6875rem;
  text-align: center;
  letter-spacing: 0.0625rem;
  flex-shrink: 0;
`

const PlanActions = styled.div`
  display: flex;
  gap: 0.25rem;
  flex-shrink: 0;
`

const ExpandedActionsContainer = styled.div`
  margin-top: 0.5rem;
  padding: 0.75rem;
  background: ${({ theme }) => theme.forecast.background3};
  border-radius: 0.375rem;
  border-left: 0.1875rem solid #ffffff;
`

const ActionsHeader = styled.div`
  font-weight: 600;
  font-size: 0.75rem;
  color: ${({ theme }) => theme.forecast.text1};
  margin-bottom: 0.5rem;
`

const ViewHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  padding: 0 0.25rem;
`

const ViewTitle = styled.h3`
  font-weight: 600;
  font-size: 1rem;
  color: ${({ theme }) => theme.forecast.text1};
  margin: 0;
`

const EmptyStateContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 3rem 1.5rem;
  text-align: center;
`

const EmptyStateText = styled.div`
  color: ${({ theme }) => theme.forecast.secondaryText};
  font-size: 0.875rem;
  margin-bottom: 1rem;
`

interface PlanRowData {
  id: string
  name: string
  preferred: boolean
  actionCount: number
  plan: GeckoAssetHealthPlan
}

export const HealthPlanningWidget: React.FC<HealthPlanningWidgetProps> = ({
  unit,
  site,
  readonly = false,
  onPlanCreated,
  onActionCreated,
  onSelectedPlanChange,
  onRiskClick,
  className,
  healthPlans,
  planToActions,
  allActions,
  components,
  riskAssessmentConfig,
  isLoading,
  forceRefresh,
  availableTags,
  deficiencies = [],
}) => {
  const {
    canEdit,
    canView,
    selectedPlanId,
    setSelectedPlanId,
    createHealthPlan,
    createAction,
    editHealthPlan,
    editAction,
  } = useHealthPlanningWidget({
    unit,
    site,
    onPlanCreated,
    onActionCreated,
    onSelectedPlanChange,
    healthPlans,
    planToActions,
    allActions,
    components,
    forceRefresh,
  })

  // Local state for plan editing and form management
  const [editingPlanId, setEditingPlanId] = useState<string | null>(null)
  const [editingPlanName, setEditingPlanName] = useState('')
  const [showCreateForm, setShowCreateForm] = useState(false)

  // Combine prop-level readonly with permissions
  const effectiveReadonly = readonly || !canEdit

  // stable sort on creation time to prevent visual reordering when preferred status changes
  const planRows: PlanRowData[] = useMemo(() => {
    const sortedPlans = [...healthPlans].sort((a, b) => {
      if (a.created_at && b.created_at) {
        return a.created_at.localeCompare(b.created_at)
      }
      return a.id.localeCompare(b.id)
    })

    return sortedPlans.map((plan) => ({
      id: plan.id,
      name: plan.data.name || 'Unnamed Plan',
      preferred: plan.data.preferred || false,
      actionCount: planToActions.get(plan.id)?.length || 0,
      plan,
    }))
  }, [healthPlans, planToActions])

  // Event handlers
  const handlePlanClick = (planId: string) => {
    if (selectedPlanId === planId) {
      setSelectedPlanId('') // Deselect if already selected
    } else {
      setSelectedPlanId(planId)
    }
  }

  const handlePreferredClick = async (e: React.MouseEvent, planId: string) => {
    e.stopPropagation()
    if (!effectiveReadonly) {
      await handlePreferredPlanToggle(planId, [...healthPlans], editHealthPlan)
    }
  }

  const handleEditClick = (
    e: React.MouseEvent,
    planId: string,
    currentName: string
  ) => {
    e.stopPropagation()
    setEditingPlanId(planId)
    setEditingPlanName(currentName)
  }

  const handleSaveEdit = async (e?: React.MouseEvent | React.KeyboardEvent) => {
    e?.stopPropagation()
    if (editingPlanId && editingPlanName.trim()) {
      await editHealthPlan(editingPlanId, {
        name: editingPlanName.trim(),
      } as Partial<GeckoAssetHealthPlan['data']>)
      setEditingPlanId(null)
      setEditingPlanName('')
    }
  }

  const handleCancelEdit = (e: React.MouseEvent) => {
    e.stopPropagation()
    setEditingPlanId(null)
    setEditingPlanName('')
  }

  const handleActionEdit = async (
    actionId: string,
    updates: Partial<GeckoAssetHealthAction['data']> & { componentId?: string }
  ) => {
    await editAction(actionId, updates)
  }

  const handleActionCreate = async (
    planId: string,
    actionData: Partial<GeckoAssetHealthAction['data']> & {
      componentId?: string
    }
  ) => {
    await createAction(planId, actionData)
  }

  const handleCreatePlan = async (planName: string) => {
    await createHealthPlan(planName)
    setShowCreateForm(false)
  }

  const handleCancelCreate = () => {
    setShowCreateForm(false)
  }

  const renderContent = () => {
    if (isLoading) {
      return (
        <LoadingContainer>
          <Spin size='large' />
        </LoadingContainer>
      )
    }

    if (!canView) {
      return (
        <ErrorContainer>
          You do not have permission to view health plans.
        </ErrorContainer>
      )
    }

    return (
      <div>
        <ViewHeader>
          <ViewTitle>Health Plans ({healthPlans.length})</ViewTitle>
          {!effectiveReadonly && (
            <Button
              type='primary'
              size='small'
              icon={<Plus size={14} />}
              onClick={() => setShowCreateForm(true)}
              disabled={isLoading}>
              Create Health Plan
            </Button>
          )}
        </ViewHeader>

        {showCreateForm && (
          <CreatePlanForm
            onCreatePlan={handleCreatePlan}
            onCancel={handleCancelCreate}
            isLoading={isLoading}
            placeholder='Enter new plan name'
            marginBottom={true}
          />
        )}

        {healthPlans.length === 0 ? (
          <EmptyStateContainer>
            <EmptyStateText>No health plans created yet</EmptyStateText>
          </EmptyStateContainer>
        ) : (
          <PlansContainer>
            {planRows.map((planRow) => {
              const isSelected = selectedPlanId === planRow.id
              const planActions = planToActions.get(planRow.id) || []

              return (
                <div key={planRow.id}>
                  <PlanRow
                    $selected={isSelected}
                    onClick={() => handlePlanClick(planRow.id)}>
                    <PreferredSelector
                      $preferred={planRow.preferred}
                      onClick={(e) => handlePreferredClick(e, planRow.id)}
                      title={
                        planRow.preferred
                          ? 'Click to remove preferred status'
                          : 'Click to make preferred'
                      }
                    />

                    <PlanNameContainer>
                      <PlanName
                        $editing={editingPlanId === planRow.id}
                        $isLong={planRow.name.length > 20}>
                        {planRow.name}
                      </PlanName>
                      {editingPlanId === planRow.id && (
                        <PlanEditInput
                          value={editingPlanName}
                          onChange={(e) => setEditingPlanName(e.target.value)}
                          onPressEnter={() => handleSaveEdit()}
                          autoFocus
                          size='small'
                        />
                      )}
                    </PlanNameContainer>

                    <StyledRiskBadge
                      riskValue={planRow.plan.data.predictedRisk}
                      riskAssessmentConfig={riskAssessmentConfig}
                      onClick={
                        onRiskClick
                          ? (e) => {
                              e?.stopPropagation()
                              onRiskClick(planRow.plan)
                            }
                          : undefined
                      }
                    />

                    <ActionCount>
                      {planRow.actionCount} action
                      {planRow.actionCount !== 1 ? 's' : ''}
                    </ActionCount>

                    {!effectiveReadonly && (
                      <PlanActions>
                        <Tooltip title='View in Budgeting'>
                          <Link
                            to='/budgeting/{-$siteID}'
                            params={{ siteID: site.id }}
                            search={{
                              search: unit.data.name,
                              showPreferredOnly: false,
                            }}
                            onClick={(e) => e.stopPropagation()}>
                            <Button
                              size='small'
                              type='text'
                              icon={<DollarSign size={12} />}
                            />
                          </Link>
                        </Tooltip>
                        {editingPlanId === planRow.id ? (
                          <>
                            <Button
                              size='small'
                              type='text'
                              icon={<Check size={12} />}
                              onClick={handleSaveEdit}
                              disabled={!editingPlanName.trim()}
                            />
                            <Button
                              size='small'
                              type='text'
                              icon={<X size={12} />}
                              onClick={handleCancelEdit}
                            />
                          </>
                        ) : (
                          <Button
                            size='small'
                            type='text'
                            icon={<Edit3 size={12} />}
                            onClick={(e) =>
                              handleEditClick(e, planRow.id, planRow.name)
                            }
                            disabled={isLoading}
                          />
                        )}
                      </PlanActions>
                    )}
                  </PlanRow>

                  {isSelected && (
                    <ExpandedActionsContainer>
                      <ActionsHeader>
                        Actions for &quot;{planRow.name}&quot;
                      </ActionsHeader>
                      <ActionsList
                        actions={planActions}
                        components={[...components]}
                        onActionEdit={handleActionEdit}
                        onActionCreate={(actionData) =>
                          handleActionCreate(planRow.id, actionData)
                        }
                        readonly={effectiveReadonly}
                        isLoading={isLoading}
                        availableTags={availableTags || []}
                        deficiencies={deficiencies}
                      />
                    </ExpandedActionsContainer>
                  )}
                </div>
              )
            })}
          </PlansContainer>
        )}
      </div>
    )
  }

  return (
    <WidgetContainer className={className}>{renderContent()}</WidgetContainer>
  )
}
